<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { VForm } from "vuetify/components";
import OmniTextarea from "@/components/omni/OmniTextarea.vue";
import StopDateRange from "@/views/orders/components/StopDateRange.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { OrderFreightState, OrderStopType } from "@/constants/order";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import { NotStartedRouteStopStatus } from "@/constants/trip";
import { useFreightOrdinals, useFreights, useOrder } from "@/composables";
import { useGoogleMap } from "@/composables/useGoogleMap";
import PickUpReloadFreights from "@/views/trips/edit/components/PickUpReloadFreights.vue";
import RouteStopFreight from "@/views/trips/edit/components/RouteStopFreight.vue";
import DeliveryRouteFreights from "@/views/trips/edit/components/DeliveryRouteFreights.vue";
import DeletedChip from "@/components/chips/DeletedChip.vue";
import { generateId } from "@/utils/generateId";
import type { OrderFreightDto } from "@/api/orders/dto/order.dto";
import type { TripRoute, TripRouteFreight, TripRouteStop, TripStop } from "@/types/trip";

const model = defineModel<boolean>({ required: true });

const props = withDefaults(
  defineProps<{
    stop: TripRouteStop;
    tripStops: TripStop[];
    routeFreights: TripRouteFreight[];
    orderFreights: OrderFreightDto[];
    allowEditUsage?: boolean;
    routes: TripRoute[];
    route: TripRoute;
    initialRouteStopFreights: TripRouteFreight[];
    routeStopFreightsFromApi: TripRouteFreight[];
    stopType: OrderStopType;
  }>(),
  {
    allowEditUsage: false,
  },
);

const { getFreightOrdinalLabel } = useFreightOrdinals(() => props.route.routeStops);

const emit = defineEmits<{
  (e: "changed", stop: TripRouteStop, freights: TripRouteFreight[]): void;
  (e: "open-stop-dialog", routeStop: TripRouteStop): void;
}>();

const { isReloadStop, timeTypesMap, getStopTime } = useOrder();
const { getFreightName } = useFreights();
const { getPlaceUrl } = useGoogleMap();

const editStopForm = ref<VForm | null>(null);
const relevantStopFreights = ref<TripRouteFreight[]>([]);
const stopData = ref<Partial<TripRouteStop> & { type?: OrderStopType }>({});

const tripStop = computed(() => {
  return props.tripStops.find((os) => {
    return os.id === props.stop.tripStopId;
  });
});

const facilityAddress = computed(() => {
  const chunks: string[] = [];
  if (props.stop.facility.addressLine) {
    chunks.push(props.stop.facility.addressLine);
  }
  if (props.stop.facility.address) {
    chunks.push(props.stop.facility.address);
  }
  return chunks.join(", ");
});

const isPickup = computed(() => {
  return props.stopType === OrderStopType.pickup;
});

const isDelivery = computed(() => {
  return props.stopType === OrderStopType.delivery;
});

const isReload = computed(() => {
  return isReloadStop(props.stop);
});

const isDeleted = computed(() => {
  return props.stop.isDeleted;
});

const wasCreated = computed(() => {
  return Boolean(props.stop?.id);
});

const freightOptions = computed(() => {
  const orderFreightsWithName = props.orderFreights
    .map((orderFreight) => {
      const routeFreight = props.routeFreights.find((rf) => rf.freightId === orderFreight.id);
      const label = getFreightOrdinalLabel(routeFreight?.id);
      const pickupIndexFreight = Number(label?.split("-")[1]) || 0;

      return {
        ...orderFreight,
        number: pickupIndexFreight + 1,
        name: getFreightName(orderFreight, label, "", false),
      };
    })
    .sort((a, b) => a.number - b.number);

  if (isReload.value) {
    return orderFreightsWithName;
  }

  return orderFreightsWithName.filter((f) => {
    return tripStop.value?.freights?.includes(f.id!);
  });
});

const changeDisabled = computed(() => {
  const isNotStartedStatus = NotStartedRouteStopStatus.includes(props.stop.status);

  if (!wasCreated.value) {
    return false;
  }

  if (isDeleted.value) {
    return true;
  }

  return !isNotStartedStatus;
});

const saveDisabled = computed(() => {
  return (isPickup.value || (isReload.value && isDelivery.value)) && relevantStopFreights.value.length === 0;
});

const primaryActionButton = computed<string | null>(() => {
  if (!props.allowEditUsage) {
    return props.stop.use ? "Save" : "Add";
  }

  if (props.stop?.id) {
    return props.stop.isDeleted ? null : "Save";
  }

  return props.stop.use ? "Save" : "Add";
});

const routeStops = computed(() => {
  return props.route.routeStops;
});

const secondaryActionLabel = computed<string | null>(() => {
  if (props.stop?.id) {
    return props.stop.isDeleted ? "Undo delete" : "Delete stop";
  }
  return props.stop.use ? "Delete stop" : null;
});

const secondaryButtonDisabled = computed(() => {
  // We can undo delete stop anytime
  if (props.stop.isDeleted) {
    return false;
  }

  // we can delete stop if we have at least one existed stop
  if (!routeStops.value.length) {
    return true;
  }

  const validStops = routeStops.value.filter((stop) => {
    return (stop.type === OrderStopType.notSelected || stop.type === props.stopType) && !stop.isDeleted;
  });

  return validStops.length <= 1;
});

const secondaryTooltipText = computed(() => {
  if (!secondaryButtonDisabled.value) {
    return "";
  }

  return "The stop cannot be deleted because the route must contain at least 1 Pick up and 1 Delivery stop";
});

const title = computed(() => {
  return props.stop.isReload ? `${props.stop.name} reload stop settings for` : `Edit route stop ${props.stop.name}`;
});

function getOrderStopsByFreight(freight: { id?: string }) {
  return props.tripStops.filter((os) => {
    return os.freights?.includes(freight?.id as string);
  });
}

function getOrderByFreight(freight: { id?: string }) {
  return getOrderStopsByFreight(freight)[0]?.order;
}

function getFreightPoints(freight: { id?: string }) {
  const stops = getOrderStopsByFreight(freight);
  return stops.map((os) => {
    return { ...os.facility, fullAddress: os.facility?.address };
  });
}

function getRouteFreightsForOrderFreight(orderFreight: OrderFreightDto) {
  return props.routeFreights
    .filter((rf) => {
      return rf.freightId === orderFreight.id;
    })
    .map((rf, i) => {
      const label = getFreightOrdinalLabel(rf.id);
      return { ...rf, number: i + 1, name: getFreightName(rf, label, "", false, true) };
    });
}

function getStopRouteFreightsForOrderFreight(orderFreight: OrderFreightDto) {
  if (isReload.value && props.stopType === OrderStopType.pickup) {
    return props.tripStops
      .find((s) => {
        return s.id === props.stop.orderStopId;
      })
      ?.reloadFreights?.filter((reloadFreight) => {
        return reloadFreight.freightId === orderFreight.id;
      });
  }

  return relevantStopFreights.value
    .filter((rf) => {
      return rf.freightId === orderFreight.id;
    })
    .map((rf, i) => {
      rf.number = i + 1;
      return rf;
    });
}

function addFreight(orderFreight: OrderFreightDto) {
  const state = props.route.id ? OrderFreightState.new : OrderFreightState.normal;

  relevantStopFreights.value.push({
    id: generateId(),
    freightId: orderFreight.id!,
    quantity: undefined,
    weight: undefined,
    stackable: orderFreight.stackable,
    type: orderFreight.type!,
    plannedWeight: 1,
    plannedQuantity: 1,
    state,
    $isNew: true,
    isDeleted: false,
  } as TripRouteFreight);
}

function onSplitFreight(parentFreight: TripRouteFreight) {
  const parentIndex = relevantStopFreights.value.findIndex((freight) => freight.id === parentFreight.id);

  if (parentIndex === -1) {
    return;
  }

  const childSplitFreight = {
    ...parentFreight,
    id: generateId(),
    weight: null,
    quantity: null,
    plannedWeight: null,
    plannedQuantity: null,
    state: props.route.id ? OrderFreightState.new : OrderFreightState.normal,
    isDeleted: false,
    splitFreightId: parentFreight.id,
  } as TripRouteFreight;

  console.log("child splitFreight", childSplitFreight);

  relevantStopFreights.value.splice(parentIndex + 1, 0, childSplitFreight);
}

function getUpdatedRouteFreights(isDeleteStop: boolean): TripRouteFreight[] {
  const stopFreightIds = new Set(props.stop.freights);
  const updateStopFreightsById = new Map(
    relevantStopFreights.value.map((f) => {
      return [f.id, f];
    }),
  );

  if (isDeleteStop && !props.stop.id && isPickup.value) {
    return props.route.freights.filter((f) => {
      return !stopFreightIds.has(f.id);
    });
  }

  let baseFreights = props.route.freights;

  if (isPickup.value) {
    baseFreights = baseFreights.filter((freight) => {
      const isRelatedToStop = stopFreightIds.has(freight.id);
      return !(isRelatedToStop && !updateStopFreightsById.has(freight.id));
    });
  }

  const updatedFreights = baseFreights.map((freight) => {
    const updated = updateStopFreightsById.get(freight.id);
    return updated ? { ...freight, ...updated } : freight;
  });

  const existingIds = new Set(
    updatedFreights.map((f) => {
      return f.id;
    }),
  );
  const newFreights = relevantStopFreights.value.filter((f) => {
    return !existingIds.has(f.id);
  });

  return [...updatedFreights, ...newFreights];
}

async function save(isDeleted = false, use = true) {
  const result = await editStopForm.value?.validate();
  if (!result?.valid) return;

  const stop = {
    ...props.stop,
    ...stopData.value,
    type: props.stopType,
    use,
    isDeleted,
    freights: relevantStopFreights.value.map((f) => {
      return f.id;
    }),
  } as TripRouteStop;

  emit("changed", stop, getUpdatedRouteFreights(isDeleted));
  model.value = false;
}

function remove() {
  save(!props.stop?.isDeleted, Boolean(props.stop.id));
}

function initStopState() {
  relevantStopFreights.value = [...props.initialRouteStopFreights];
  stopData.value = {
    timeType: props.stop.timeType,
    timezone: props.stop.timezone,
    timeFrom: props.stop.timeFrom,
    timeTo: props.stop.timeTo,
    time2Type: props.stop.time2Type,
    time2From: props.stop.time2From,
    time2To: props.stop.time2To,
    type: props.stopType,
  };
}

function resetStopState() {
  relevantStopFreights.value = [];
  stopData.value = {};
}

watch(
  model,
  (value) => {
    if (value) {
      initStopState();
    } else {
      resetStopState();
    }
  },
  { immediate: true },
);

watch(
  () => props.stop,
  () => {
    if (model.value) {
      initStopState();
    }
  },
);
</script>

<template>
  <omni-dialog
    v-model="model"
    :disabled="saveDisabled"
    :primary-action-label="primaryActionButton"
    :secondary-action-label="secondaryActionLabel"
    :secondary-button-disabled="secondaryButtonDisabled"
    :secondary-tooltip-text="secondaryTooltipText"
    :title="title"
    has-secondary-button-tooltip
    width="1200"
    @primary-action:click="save"
    @secondary-action:click="remove"
  >
    <template v-if="stop.isReload && route.truck?.id" #title:append>
      <router-link
        :to="{ name: 'truck-view', params: { id: route.truck?.id } }"
        class="text-primary text-decoration-none ml-1"
        target="_blank"
      >
        Truck#{{ route.truck.number }}
      </router-link>
    </template>
    <v-form ref="editStopForm">
      <div class="mb-4">
        <v-text-field
          :model-value="stop.facility.name"
          autocomplete="off"
          class="required"
          density="compact"
          hide-details
          label="Facility"
          readonly
          variant="outlined"
        />
      </div>
      <div class="bg-grey-lighten-5 pa-3 rounded mb-6">
        <div class="text-grey-darken-3 font-size-16 mb-2">{{ facilityAddress }}</div>
        <div class="d-flex align-center mb-6">
          <v-icon class="mr-2" color="green" size="16" small> mdi-map-marker-radius-outline</v-icon>
          <a
            :href="getPlaceUrl(stop.facility.addressCoordinates)"
            class="text-green caption text-decoration-underline"
            target="_blank"
          >
            Lat: {{ stop.facility.addressCoordinates.lat }} Lon: {{ stop.facility.addressCoordinates.lon }}
          </a>
        </div>
        <omni-textarea label="Facility note" readonly rows="2" variant="filled"></omni-textarea>
      </div>

      <div class="mb-6">
        <pick-up-reload-freights
          v-if="isReload && isPickup"
          v-model="relevantStopFreights"
          :freights-from-api="routeStopFreightsFromApi"
          :routes="routes"
          :stop="stop"
          :trip-stops="tripStops"
        />
        <template v-else>
          <div class="text-grey-darken-3 font-size-16 font-weight-500">Freight(s) from order:</div>
          <div
            v-for="orderFreight in freightOptions"
            :key="orderFreight.id"
            :class="{
              'freight--deleted': orderFreight.isDeleted,
              'freight--new': orderFreight.state === OrderFreightState.new,
              'freight--updated': orderFreight.state === OrderFreightState.updated,
            }"
            class="freight mt-4 rounded-b"
          >
            <div class="freight--header bg-grey-lighten-4 pa-4">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <div class="text-grey-darken-3 font-weight-500 font-size-16 freight--name mr-1">
                    {{ orderFreight.name }}
                  </div>
                  <span v-if="orderFreight.isDeleted" class="text-grey font-weight-500 ml-4">
                    Order #{{ getOrderByFreight(orderFreight)?.number }}
                  </span>
                  <router-link
                    v-else
                    :to="{ name: 'order-view', params: { id: getOrderByFreight(orderFreight)?.id } }"
                    class="text-decoration-none text-primary font-weight-500 ml-4"
                    target="_blank"
                  >
                    Order #{{ getOrderByFreight(orderFreight)?.number }}
                  </router-link>
                </div>

                <deleted-chip v-if="orderFreight.isDeleted" />
              </div>

              <div class="text-grey-darken-1 d-flex align-center freight--points">
                <template v-for="(point, i) in getFreightPoints(orderFreight)" :key="point.id">
                  {{ point.fullAddress }}
                  <v-icon
                    v-if="i < getFreightPoints(orderFreight).length - 1"
                    class="mx-1"
                    icon="mdi-arrow-right"
                    size="14"
                  ></v-icon>
                </template>
              </div>

              <alert-message
                v-if="orderFreight.isDeleted"
                bg-color="indigo-lighten-5"
                border-color="none"
                class="mt-4"
                color="indigo"
                icon="mdi-information-outline"
                icon-color="indigo"
              >
                Freight#{{ orderFreight.number }} will be removed after re-dispatch confirmation
              </alert-message>
            </div>

            <div v-if="isPickup" class="bg-grey-lighten-5 freight--form pa-4 freight-border rounded-b">
              <alert-message v-if="orderFreight.state === OrderFreightState.updated" class="mb-4">
                Please update route freight information
              </alert-message>
              <div class="text-grey-darken-2 mb-4 freight--add-label font-weight-500">
                Add route freight for order freight:
              </div>

              <template
                v-for="routeFreight in getStopRouteFreightsForOrderFreight(orderFreight)"
                :key="routeFreight.id"
              >
                <route-stop-freight
                  v-model="relevantStopFreights"
                  :freights-from-api="routeStopFreightsFromApi"
                  :route-freight="routeFreight"
                  :route-stop="stop"
                  @split="onSplitFreight"
                />
              </template>

              <v-btn
                :disabled="orderFreight.isDeleted || changeDisabled"
                class="px-2 mt-4"
                data-qa="add-route-freight-button"
                density="compact"
                variant="text"
                @click="addFreight(orderFreight)"
              >
                <div class="d-flex align-center text-primary text-uppercase font-size-11 font-weight-600">
                  <v-icon class="mr-2" icon="mdi-plus"></v-icon>
                  Add route freight
                </div>
              </v-btn>
            </div>

            <delivery-route-freights
              v-if="isDelivery"
              v-model="relevantStopFreights"
              :route-freights="getRouteFreightsForOrderFreight(orderFreight)"
              :route-stops="routeStops"
              :stop="stop"
              @open-stop-dialog="(stop) => emit('open-stop-dialog', stop)"
            />
          </div>
        </template>
      </div>

      <div>
        <div class="d-flex align-center text-grey-darken-1 font-weight-500 mb-4">
          <v-icon class="mr-2" icon="mdi-clock-outline" size="16"></v-icon>
          {{ stop.isReload ? "Trip reload" : "Order" }} stop time:
          {{ stop.timeType ? timeTypesMap[stop.timeType] : "" }}:
          {{ getStopTime(stop) }}
        </div>
        <stop-date-range
          v-model="stopData"
          :disabled="changeDisabled"
          :hide2-time-frame="stop.isReload"
          multiline
        ></stop-date-range>
      </div>
    </v-form>
  </omni-dialog>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.freight {
  &.freight--deleted {
    .freight--header,
    .freight--form {
      background-color: $grey-input-bg !important;
    }

    .freight--name,
    .freight--points,
    .freight--add-label,
    .freight--route-freight-name {
      color: $grey-lighter-contrast !important;
    }

    .freight--name {
      position: relative;

      &:after {
        content: "";
        position: absolute;
        width: 6px;
        height: 6px;
        display: block;
        top: 50%;
        right: -12px;
        border-radius: 50%;
        transform: translateY(-50%);
        text-decoration: none !important;
        background-color: $grey-light;
      }
    }
  }

  &.freight--new {
    border: 1px solid $green;

    .freight--header {
      background-color: $green-lighten !important;
    }

    .freight--name {
      position: relative;

      &:after {
        position: absolute;
        content: "•";
        font-size: 20px;
        text-decoration: none !important;
        color: $green;
        top: 50%;
        transform: translateY(-50%);
        right: -12px;
      }
    }
  }

  &.freight--updated {
    border: 1px solid $warning;

    .freight--header {
      background-color: $warning-light !important;
    }

    .freight--name {
      position: relative;

      &:after {
        position: absolute;
        content: "•";
        font-size: 20px;
        text-decoration: none !important;
        color: $warning;
        top: 50%;
        transform: translateY(-50%);
        right: -12px;
      }
    }
  }

  &-border {
    border-left: 1px solid #e0e0e0;
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
  }
}
</style>
